import formatMomentLabel from '../formatMomentLabel';

/**
 * summarizeRespondentOverlap — who is actually behind a growth number
 *
 * The growth figure on an effect is the difference between two group averages:
 * the first measure moment and the last one. Those two groups need not contain
 * the same people. A participant who was already taking part when the
 * organisation started measuring cannot fill in a baseline at all — their
 * first questionnaire is a follow-up — so the "before" group fills up with new
 * intake while the "after" group holds people who have been there longer.
 * Measured across production, 15 of the 32 organisations showing a growth
 * figure had NO participant in both groups.
 *
 * That does not make the figure wrong — it is an average, and an unpaired one
 * is still an average. It does mean the deep analysis has to say what it rests
 * on, so nobody reads a group difference as a decline.
 *
 * "Nobody shared" and "we cannot tell" are deliberately different answers:
 * an anonymous questionnaire carries no person on its records, and reporting 0
 * there would assert the groups differ when we simply do not know.
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 *
 * @typedef {object} RespondentOverlap
 * @property {number} firstPosition - Position of the earliest measure moment.
 * @property {number} lastPosition - Position of the latest measure moment.
 * @property {string} firstLabel - Display label of the earliest moment.
 * @property {string} lastLabel - Display label of the latest moment.
 * @property {number} firstRespondents - Identifiable people at the earliest moment.
 * @property {number} lastRespondents - Identifiable people at the latest moment.
 * @property {number|null} shared - People present at BOTH; null when it cannot be established.
 * @property {boolean} identifiable - Whether the overlap could be established at all.
 * @property {number} unidentifiedAnswers - Answers at those two moments carrying no person.
 * @property {number} orphanedAnswers - Answers whose measure moment no longer exists.
 */

/**
 * Whether this record holds an actual answer.
 *
 * A measure moment can carry stub records with an empty `Input` — a form that
 * was opened but never filled in. Those must not make a moment count as the
 * last one, or the note would describe a moment the growth figure never used.
 * A record without an `Input` key at all (non-questionnaire shapes) counts.
 * AI-CONTEXT: See docs/pitfalls.md#database-empty-string-is-a-real-stored-value
 */
const isAnswered = (record) => record?.Input !== '' && record?.Input !== null;

/** Position of a record, or null when its moment cannot be placed on the timeline. */
const positionOf = (record, id2pos) => {
    let position = record?.MomentPosition;
    if (position == null) position = id2pos[record?.MomentID];
    if (typeof position !== 'number') position = parseInt(position, 10);
    return Number.isNaN(position) ? null : position;
};

/**
 * Summarize which people sit behind the first and the last measure moment.
 *
 * Deduped by `respondentKey` — `Persona` by default, because that identifies a
 * PERSON. `FormID` identifies one submission, so it can never match across two
 * moments and would report an overlap of 0 for everyone.
 *
 * @param {Array<Object>} records - Response records (hierarchy-enriched).
 * @param {object} [options]
 * @param {Object<string, number>} [options.id2pos] - MomentID → Position, for records lacking MomentPosition.
 * @param {Object<number, string>} [options.pos2title] - Position → Title, for labels.
 * @param {string} [options.respondentKey='Persona'] - Field identifying one person.
 * @returns {RespondentOverlap|null} null when there are fewer than two moments to compare.
 */
const summarizeRespondentOverlap = (
    records,
    { id2pos = {}, pos2title = {}, respondentKey = 'Persona' } = {}
) => {
    const list = Array.isArray(records) ? records : [];

    /** @type {Map<number, Set<string>>} */
    const peopleByPosition = new Map();
    /** @type {Map<number, number>} */
    const anonymousByPosition = new Map();
    let orphanedAnswers = 0;

    list.forEach((record) => {
        if (!isAnswered(record)) return;
        const position = positionOf(record, id2pos);
        if (position == null) {
            orphanedAnswers += 1;
            return;
        }
        if (!peopleByPosition.has(position)) {
            peopleByPosition.set(position, new Set());
            anonymousByPosition.set(position, 0);
        }
        const person = record?.[respondentKey];
        if (person) peopleByPosition.get(position).add(person);
        else anonymousByPosition.set(position, anonymousByPosition.get(position) + 1);
    });

    const positions = [...peopleByPosition.keys()].sort((a, b) => a - b);
    if (positions.length < 2) return null;

    const firstPosition = positions[0];
    const lastPosition = positions[positions.length - 1];
    const firstPeople = peopleByPosition.get(firstPosition);
    const lastPeople = peopleByPosition.get(lastPosition);

    // Overlap is only a finding when both sides actually name their people.
    const identifiable = firstPeople.size > 0 && lastPeople.size > 0;
    const shared = identifiable
        ? [...lastPeople].filter((person) => firstPeople.has(person)).length
        : null;

    return {
        firstPosition,
        lastPosition,
        firstLabel: formatMomentLabel(pos2title[firstPosition], firstPosition),
        lastLabel: formatMomentLabel(pos2title[lastPosition], lastPosition),
        firstRespondents: firstPeople.size,
        lastRespondents: lastPeople.size,
        shared,
        identifiable,
        unidentifiedAnswers:
            anonymousByPosition.get(firstPosition) + anonymousByPosition.get(lastPosition),
        orphanedAnswers,
    };
};

export default summarizeRespondentOverlap;
